import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { formatApiMessage } from '@/api/client';
import { checklistsApi, type ChecklistAnswers, type ChecklistSchema } from '@/api/checklists.api';

import { ChecklistFormView } from './ChecklistFormView';
import { ChecklistTableView } from './ChecklistTableView';

type Props = {
  title: string;
  subtitle?: string;
  schema: ChecklistSchema;
  answers?: ChecklistAnswers;
  /** Reviewing a blank template — the Answer column is dropped. */
  blank?: boolean;
  /** Which side the Excel download hits. */
  download?: { kind: 'template' | 'response'; id: string };
  headerRight?: React.ReactNode;
};

/**
 * Read-only checklist reviewer with the Form ⇄ Table toggle the customer
 * asked for, plus an Excel download of exactly what the table shows.
 */
export function ChecklistReview({
  title,
  subtitle,
  schema,
  answers = {},
  blank,
  download,
  headerRight,
}: Props) {
  const { t } = useTranslation();
  const [view, setView] = useState<'form' | 'table'>('form');
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!download) return;
    setDownloading(true);
    try {
      const filename = `${title.replace(/[^\w.\- ]+/g, '_') || 'checklist'}.xlsx`;
      if (download.kind === 'template') {
        await checklistsApi.downloadTemplateXlsx(download.id, filename);
      } else {
        await checklistsApi.downloadResponseXlsx(download.id, filename);
      }
    } catch (err) {
      toast.error(formatApiMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="text-[13px] text-muted mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {headerRight}
          <div className="inline-flex rounded-md border border-border-strong overflow-hidden">
            <ViewButton active={view === 'form'} onClick={() => setView('form')}>
              📝 {t('checklist.view_form')}
            </ViewButton>
            <ViewButton active={view === 'table'} onClick={() => setView('table')}>
              ▦ {t('checklist.view_table')}
            </ViewButton>
          </div>
          {download && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="btn btn-secondary btn-sm"
            >
              {downloading ? t('common.loading') : `⬇ ${t('checklist.download_excel')}`}
            </button>
          )}
        </div>
      </div>

      {view === 'form' ? (
        <ChecklistFormView schema={schema} answers={answers} readOnly onChange={() => {}} />
      ) : (
        <ChecklistTableView schema={schema} answers={answers} hideAnswers={blank} />
      )}
    </div>
  );
}

function ViewButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${
        active ? 'bg-primary text-white' : 'bg-white text-muted hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}
